import { useState } from "react";
import { Plus, IdCard } from "lucide-react";
import { foApi } from "@/webapp/fleet-owners/api";
import RecordForm from "@/webapp/fleet-owners/tabs/RecordForm";
import { PageHead, Panel, Tag, EmptyState, Button, DataTable, Td, ExpiryCell } from "@/webapp/fleet-owners/ui";

const BLANK = { name: "", email: "", phone: "", licence_class: "", licence_expiry: "", medical_expiry: "", vehicle_id: "", status: "active" };
const DTONE = { active: "ok", on_leave: "warn", inactive: "mute" };

const fieldsFor = (vehicles) => [
  { k: "name", label: "Full name", placeholder: "Jordan Lee" },
  { k: "email", label: "Email", type: "email", placeholder: "driver@company.com" },
  { k: "phone", label: "Phone", placeholder: "+1 403 555 0100" },
  { k: "licence_class", label: "Licence class", placeholder: "Class 1" },
  { k: "licence_expiry", label: "Licence expiry", type: "date" },
  { k: "medical_expiry", label: "Medical expiry", type: "date" },
  { k: "vehicle_id", label: "Assigned truck", options: [["", "Unassigned"], ...vehicles.map((v) => [v.id, v.unit])] },
  { k: "status", label: "Status", options: ["active", "on_leave", "inactive"] },
];

export default function Drivers({ data, reload, go }) {
  const [editing, setEditing] = useState(null);
  const save = async (form) => {
    if (editing.id) await foApi.updateDriver(editing.id, form); else await foApi.addDriver(form);
    setEditing(null);
    await reload();
  };
  const remove = async (d) => { if (window.confirm(`Remove ${d.name}?`)) { await foApi.removeDriver(d.id); await reload(); } };
  const unit = (id) => data.vehicles.find((v) => v.id === id)?.unit;
  return (
    <>
      <PageHead eyebrow="Fleet" title="Drivers" sub="Roster, licences, medicals and truck assignments. Expiring documents raise alerts 60 days ahead.">
        <Button variant="primary" testid="fo-driver-add" onClick={() => setEditing({ ...BLANK })}><Plus className="h-4 w-4" />Add driver</Button>
      </PageHead>
      {editing && <RecordForm key={editing.id || "new"} prefix="fo-driver" fields={fieldsFor(data.vehicles)} initial={editing} onSave={save} onCancel={() => setEditing(null)} />}
      {data.drivers.length ? (
        <Panel className="fo-rise fo-rise-1" testid="fo-drivers-panel" title={`${data.drivers.length} drivers`} action={!data.telematics.connected && <button data-testid="fo-drivers-hos-connect" onClick={() => go("connections")} className="text-xs font-semibold text-[#FF5722] hover:underline">Connect telematics for hours-of-service</button>}>
          <DataTable head={["Driver", "Contact", "Class", "Licence", "Medical", "Truck", "Status", ""]} testid="fo-drivers-table">
            {data.drivers.map((d) => (
              <tr key={d.id} data-testid={`fo-driver-row-${d.id}`}>
                <Td strong>{d.name}</Td>
                <Td>{d.email || d.phone || "—"}</Td>
                <Td>{d.licence_class || "—"}</Td>
                <Td><ExpiryCell date={d.licence_expiry} warn={60} testid={`fo-driver-licence-${d.id}`} /></Td>
                <Td><ExpiryCell date={d.medical_expiry} warn={60} /></Td>
                <Td mono>{unit(d.vehicle_id) || "—"}</Td>
                <Td><Tag tone={DTONE[d.status]}>{String(d.status).replace(/_/g, " ")}</Tag></Td>
                <Td className="text-right">
                  <Button variant="ghost" testid={`fo-driver-edit-${d.id}`} onClick={() => setEditing({ ...BLANK, ...d, vehicle_id: d.vehicle_id || "", medical_expiry: d.medical_expiry || "" })}>Edit</Button>
                  <Button variant="ghost" testid={`fo-driver-remove-${d.id}`} onClick={() => remove(d)}>Remove</Button>
                </Td>
              </tr>
            ))}
          </DataTable>
        </Panel>
      ) : !editing && <EmptyState icon={IdCard} testid="fo-drivers-empty" title="No drivers yet" body="Add drivers to assign them to trucks and bookings and track licence and medical expiry."><Button variant="primary" testid="fo-driver-add-empty" onClick={() => setEditing({ ...BLANK })}>Add driver</Button></EmptyState>}
    </>
  );
}
